import { useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../lib/api";
import { AuthLayout } from "../components/AuthLayout";
import { Field } from "../components/Field";
import type { MePublic } from "../lib/types";

export function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const qc = useQueryClient();
  const nav = useNavigate();
  const location = useLocation();
  const from =
    ((location.state as { from?: { pathname?: string } } | null)?.from
      ?.pathname as string) || "/";

  const mut = useMutation({
    mutationFn: (body: { email: string; password: string }) =>
      api.post<MePublic>("/api/auth/login", body),
    onSuccess: (data) => {
      qc.setQueryData(["me"], data);
      nav(from, { replace: true });
    },
    onError: (e: unknown) => {
      if (e instanceof ApiError) setError(e.detail);
      else setError("Something went wrong.");
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    mut.mutate({ email, password });
  };

  return (
    <AuthLayout
      kicker="N° 01 — Entry"
      title={
        <>
          Welcome <span className="display-italic">back</span>
        </>
      }
      tagline="Step back into the kitchen — the kettle's still warm."
    >
      <form onSubmit={onSubmit} className="space-y-8">
        <Field
          label="Email"
          value={email}
          onChange={setEmail}
          type="email"
          autoComplete="email"
          required
          delay={320}
        />
        <Field
          label="Password"
          value={password}
          onChange={setPassword}
          type="password"
          autoComplete="current-password"
          required
          delay={400}
        />

        {error && (
          <div className="border-l-2 border-sienna pl-4 font-body italic text-sienna animate-fade-in">
            {error}
          </div>
        )}

        <div
          className="flex flex-wrap items-center gap-8 pt-2 animate-fade-up"
          style={{ animationDelay: "480ms" }}
        >
          <button type="submit" className="btn-ink" disabled={mut.isPending}>
            {mut.isPending ? "Entering…" : "Sign in"}
          </button>
          <Link to="/signup" className="label ink-link text-ink-walnut">
            New here? Begin a journal
          </Link>
        </div>
      </form>
    </AuthLayout>
  );
}
